<template>
  <div class="landing-view">
    <!-- Top Section: Your services & Admin options -->
    <section class="landing-section landing-section--dark">
      <div class="landing-container">
        <h1>Services</h1>
        <div class="landing-services-header">
          <h2 class="landing-section-title">Subscribed services</h2>
          <svg class="landing-section-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>

        <!-- Three column layout using flex-grow -->
        <div class="landing-three-column">
          <!-- Left 2 columns: Services + Admin -->
          <div class="landing-left-columns">
            <!-- Blue service cards in 2-column grid -->
            <div class="landing-services-grid">
              <ServiceCard
                v-for="service in subscribedServices"
                :key="service.name"
                :title="service.title"
                :description="service.description"
                variant="blue"
                :routeTo="service.routeTo"
                @click="handleServiceClick"
              />
            </div>

            <!-- Admin options section -->
            <div class="landing-admin-section">
              <div class="landing-services-header">
                <h2 class="landing-section-title">Admin options</h2>
                <svg class="landing-section-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>

              <div class="landing-admin-grid">
                <ServiceCard
                  title="Dashboards & Reporting"
                  description="Track your order and access standard and customised reports"
                  variant="teal"
                  routeTo="/dashboards"
                  @click="handleAdminClick"
                />
                <ServiceCard
                  title="User Admin"
                  description="Manage user settings, permissions"
                  variant="teal"
                  routeTo="/list-view-drawer/users"
                  @click="handleAdminClick"
                />
              </div>
            </div>
          </div>

          <!-- Right column: Announcements -->
          <div class="landing-right-column">
            <AnnouncementCard 
              :announcements="announcements"
              :isLoading="isLoadingAnnouncements"
              @refresh="handleAnnouncementRefresh"
            />
          </div>
        </div>
      </div>
    </section>

    <OtherService />
  </div>
</template>

<script setup>
import { onBeforeMount, computed } from 'vue'
import ServiceCard from '@/components/Landing/ServiceCard.vue'
import OtherService from '@/components/Landing/OtherService.vue'
import AnnouncementCard from '@/components/Landing/AnnouncementCard.vue'
import { usePathController } from '@/composables/usePathController'
import { useAnnouncementStore } from '@/store/announcement'
import { useUserStore } from '@/store/user'
import {useLogger} from "@/composables/useLogger"
const {sendLog} = useLogger() 

const { goTo } = usePathController()
const announcementStore = useAnnouncementStore()
const userStore = useUserStore()

const subscribedServices = computed(() => userStore.approvedSubsystem)
const announcements = computed(() => announcementStore.getCachedAnnouncements)
const isLoadingAnnouncements = computed(() => announcementStore.isAnyLoading)

onBeforeMount(async () => {
  try {
    await userStore.getCurrentUser()
    sendLog("debug", "user_profile_loads", {checkPassword: userStore.isPasswordExpired})
    if(userStore.isPasswordExpired === "true") {
      goTo("/list-view-drawer/users", { edit: userStore.userInfo?._id, passwordExpired: true })
    }
    await announcementStore.fetchAnnouncements()
  } catch (error) {
    console.error('Error fetching Announcements:', error)
  }
})

const handleServiceClick = (route) => {
  sendLog("debug", "select_service", {route})
  if (route) {
    goTo(route)
  }
}

const handleAdminClick = (route) => {
  sendLog("debug", "select_admin_service", {route})
  if (route) {
    goTo(route)
  }
}

const handleAnnouncementRefresh = async () => {
  try {
    // Force refresh to bypass cache
    await announcementStore.fetchAnnouncements({forceRefresh: true})
    } catch (error) {
    console.error('Error refreshing announcements:', error)
  }
}
</script>

<style scoped>
.landing-view {
  min-height: 100vh;
}

/* Loading State */
.landing-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1rem;
  color: white;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #f6b93b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.landing-loading p {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
}

.landing-section {
  padding: 3rem 0 3rem 0;
}

.landing-section--dark {
  background: linear-gradient(135deg, #1e3a5f 0%, #2c4f7c 100%);
  color: white;
}

.landing-section--light {
  background: #f5f7fa;
  color: #2d3748;
}

.landing-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.landing-services-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.landing-section-title {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
}

.landing-section-title--dark {
  color: #2d3748;
}

.landing-section-icon {
  color: #f6b93b;
  flex-shrink: 0;
}

/* Three column layout using flexbox */
.landing-three-column {
  display: flex;
  gap: 1.5rem;
  align-items: stretch;
}

.landing-left-columns {
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.landing-right-column {
  flex: 1;
  display: flex;
}

/* Services grid - 2 columns within left section */
.landing-services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

/* Admin section */
.landing-admin-section {
  display: flex;
  flex-direction: column;
}

.landing-admin-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}


/* Responsive breakpoints */
@media (max-width: 1024px) {
  .landing-three-column {
    flex-direction: column;
  }

  .landing-left-columns {
    flex: 1;
  }

  .landing-right-column {
    flex: 1;
  }

  .landing-services-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing-admin-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
